import { useEffect, useState } from 'react';
import { useOnboardingStepStatus } from './useOnboardingStepStatus';
import { useOnboardingSessionStorage } from './useOnboardingSessionStorage';

const STEP_KEYS = {
    profileImage: 'onboarding-dashboard.step.profile-image.ready-for-next',
    socialLinks: 'onboarding-dashboard.step.social-links.ready-for-next',
    depositSetup: 'onboarding-dashboard.step.deposit-setup.ready-for-next',
    firstProduct: 'onboarding-dashboard.step.first-product.ready-for-next',
} as const;

/**
 * Hook to determine the current active onboarding step based on completion status
 * Returns the step number (1-4) that should be active
 */
export function useOnboardingCurrentStep() {
    const { isProfileImageUploaded, isSocialLinksAdded, isFirstProductAdded, isDirectDepositAdded } =
        useOnboardingStepStatus();

    const { refreshKey } = useOnboardingSessionStorage();

    const [currentStep, setCurrentStep] = useState(1);

    useEffect(() => {
        // Read step readiness from sessionStorage
        const step1Ready = sessionStorage.getItem(STEP_KEYS.profileImage) === '1';
        const step2Ready = sessionStorage.getItem(STEP_KEYS.socialLinks) === '1';
        const step3Ready = sessionStorage.getItem(STEP_KEYS.depositSetup) === '1';

        // Check if each step is completed (either actual completion OR sessionStorage entry exists)
        const step1Completed = isProfileImageUploaded || step1Ready;
        const step2Completed = isSocialLinksAdded || step2Ready;
        const step3Completed = isDirectDepositAdded || step3Ready;

        // Determine current active step
        if (!step1Completed) {
            setCurrentStep(1);
        } else if (!step2Completed) {
            setCurrentStep(2);
        } else if (!step3Completed) {
            setCurrentStep(3);
        } else {
            setCurrentStep(4);
        }
    }, [isProfileImageUploaded, isSocialLinksAdded, isDirectDepositAdded, isFirstProductAdded, refreshKey]);

    return currentStep;
}
